import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
} from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { Dialog } from 'primeng/dialog';
import { DividerModule } from 'primeng/divider';
import { FloatLabelModule } from 'primeng/floatlabel';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { TextareaModule } from 'primeng/textarea';
import { CompanyService } from '../../../../services/remote/company.service';
import { LicenseService } from '../../../../services/remote/license.service';
import { NotificationService } from '../../../../services/shared/notification.service';
import { QueryResult } from '../license/types/licenseTypes';

@Component({
  selector: 'cognos-license-form-dialog',
  imports: [
    ButtonModule,
    TableModule,
    TagModule,
    FormsModule,
    ReactiveFormsModule,
    FloatLabelModule,
    InputTextModule,
    TextareaModule,
    DividerModule,
    InputNumberModule,
    SelectModule,
    Dialog,
  ],
  templateUrl: './license-form-dialog.component.html',
  styleUrl: './license-form-dialog.component.scss',
})
export class LicenseFormDialogComponent implements OnChanges {
  @Input({ required: true }) companyId: string | undefined;
  @Input() license: any;
  @Output() licenseChange = new EventEmitter<any>();

  @Input() isOpen: boolean = false;
  @Output() isOpenChange = new EventEmitter<boolean>();

  public isEditing: boolean = false;
  public licenseId: string | undefined = undefined;
  public formGroup!: FormGroup;
  public statusOptions = this._licenseService.statusOptions$;

  constructor(
    private _companyService: CompanyService,
    private _licenseService: LicenseService,
    private _notificationService: NotificationService,
    private fb: FormBuilder
  ) {
    const defaultStatus = this.statusOptions.find(
      (status) => status.code === 'ok'
    );

    this.formGroup = this.fb.group({
      name: ['', Validators.required],
      address: [
        '',
        [Validators.required, Validators.pattern(/^https?:\/\/.+\/$/)],
      ],
      authAddress: [
        '',
        [Validators.required, Validators.pattern(/^https?:\/\/.+\/$/)],
      ],
      status: [defaultStatus, Validators.required],
    });

    this.isEditing = false;
  }

  ngOnChanges(changes: any) {
    if (changes.license) {
      if (changes.license.currentValue) {
        const licenseToEdit = changes.license.currentValue;
        this.updateFormWithLicense(licenseToEdit);
        this.licenseId = licenseToEdit.id;
        this.isEditing = true;
      } else {
        this.licenseId = undefined;
        this.isEditing = false;
      }
    }
  }

  updateFormWithLicense(license: any) {
    this.formGroup.setValue({
      name: license.name,
      address: license.address,
      authAddress: license.authAddress,
      status: this.statusOptions.find(
        (status) => status.code === license.status
      ),
    });
  }

  public async onSubmitLicense(licenseForm: any) {
    if (!this.companyId) return;

    if (this.isEditing) {
      this._updatePreviousLicense(licenseForm);
    } else {
      this._createNewLicense(licenseForm);
    }
  }

  private async _createNewLicense(licenseForm: any) {
    const result = await this._licenseService.addLicenseToCompany(
      licenseForm,
      this.companyId!
    );
    this._manageSaveResponse(result);
  }

  private async _updatePreviousLicense(licenseForm: any) {
    if (!this.licenseId || !this.companyId) return;

    const result = await this._licenseService.editLicense(
      licenseForm,
      this.licenseId,
      this.companyId
    );
    this._manageSaveResponse(result);
  }

  private _manageSaveResponse(result: any) {
    if (result === QueryResult.ok) {
      this._companyService.fetchCompanies();
      this.closeModal();
      this._notificationService.showSuccessNotification(
        'Data was successfully saved.'
      );
    } else {
      this._notificationService.showErrorNotification(
        'Error saving changes. Please try again.'
      );
      console.error('Error', result);
    }
  }

  public closeModal() {
    this.isOpenChange.emit(false);
    this.formGroup.reset();
    this.licenseChange.emit(undefined);
  }
}
